/**
 * PlatformNoticeBar - the non-dismissable platform-wide security notice.
 *
 * Shown while the platform runs a rolled-back release for security reasons
 * (SPEC-OPERATIONS RULE-019, the CRA Art. 14(8) user-notification channel;
 * STORY-SCR-PLATFORM-NOTICE AC-003/AC-004). Mounted once per surface in the
 * admin, tenant and workspace SPA shells, and in the public widget only when
 * the tenant's channel flag enables it - a gate enforced SERVER-side, never
 * here.
 *
 * FOUR STATES, two of which render nothing (RULE-042, applied to a single-fact
 * fetch rather than a list):
 *   - loading  -> renders NOTHING. A skeleton for a bar that is absent more
 *                 than 99% of the time would itself be a layout-shift nuisance.
 *   - error    -> renders NOTHING visible; logs once. This is a NAMED, and
 *                 deliberate, exception to "never swallow a failed fetch":
 *                 RULE-042's usual reading protects an OPERATOR who needs to
 *                 know a fetch broke. This component's audience is every end
 *                 user of every SPA and every public chat visitor, for whom a
 *                 visible error banner about a notice they were never going to
 *                 see is strictly worse than the rare condition it reports -
 *                 which an administrator sees elsewhere, on the Compliance page.
 *   - hidden   -> `active: false` renders null, no DOM node at all.
 *   - ready    -> `active: true` renders the bar.
 *
 * SECURITY: the notice text is SERVER-STORED and reaches the DOM through JSX
 * text interpolation ONLY - never `dangerouslySetInnerHTML`, never an
 * HTML-parsing library - so an injected `<script>` renders as the literal
 * string and cannot execute. This is the render-side half of AC-003's
 * script-injection golden test; the write-side reject lives in the gateway's
 * PUT handler, so the two layers are defense in depth rather than one point of
 * failure.
 *
 * NON-DISMISSABLE: the component has NO interactive control at all - no close
 * button and no stored dismissal - because RULE-019 lets an administrator edit
 * the text but never dismiss the notice, and it clears only on the next clean
 * deploy.
 *
 * ONE LINGUI MESSAGE: the notice text originates server-side per locale
 * (text_hu / text_en), so it is stored content, not a message catalog entry.
 * The only static string is the bar's accessible region label, a Lingui
 * message read through `useLingui` like any other component's: every shell
 * that mounts the bar (admin, tenant, workspace, and the public widget's
 * index.html) sits under the app's I18nProvider, which renders nothing until
 * a catalog is active. It used to be an ASCII-folded Hungarian literal
 * ("Platform biztonsagi ertesites") chosen by locale; the 2026-09-29 English
 * UI sweep (finding #40) moved it into the catalog so it reads as proper
 * Hungarian.
 *
 * @module components/shared/PlatformNoticeBar
 * @see TASK-SCR-PLATFORM-NOTICE-BAR
 */
import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getContrastRatio, type Theme } from "@mui/material/styles";
import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";

import {
  getPlatformNotice,
  type PlatformNoticeResponse,
} from "../../../api/platformNotice";

/** Poll cadence, matching the route's own `Cache-Control: max-age=30`. */
const POLL_INTERVAL_MS = 30_000;

/**
 * Consecutive failures after which the bar stops polling for the rest of the
 * page's life. A notice fetch is not worth an unbounded retry loop against a
 * gateway that is evidently unwell; a page reload starts it again.
 */
const MAX_CONSECUTIVE_FAILURES = 5;

/** Backoff multiplier cap, so a degraded gateway is polled every 8 minutes, not every 30 seconds. */
const MAX_BACKOFF_MULTIPLIER = 16;

export interface PlatformNoticeBarProps {
  /**
   * The public widget token, present ONLY when this bar is mounted inside the
   * public widget. The three authenticated SPA shells leave it undefined: the
   * channel flag governs the public widget surface specifically, and an
   * authenticated preview is not that surface.
   */
  publicWidgetToken?: string;
}

/** Which stored text to show for the active locale. */
function selectText(notice: PlatformNoticeResponse): string {
  const locale = (i18n?.locale ?? "").toLowerCase();
  const hu = notice.text_hu ?? "";
  const en = notice.text_en ?? "";
  if (locale.startsWith("hu")) {
    return hu || en;
  }
  return en || hu;
}

/**
 * Non-dismissable platform-wide security notice bar.
 *
 * Renders `null` unless the platform reports an active notice, so on every
 * ordinary day the surrounding layout is byte-identical to a shell without it.
 */
/**
 * The bar's text colour, chosen by contrast against its own background
 * (TASK-BUGFIX-WIDGET-NOTICE-CONTRAST; WCAG 1.4.3). The widget and the permalink
 * mount no ThemeProvider, so MUI's default palette applies there, and its
 * warning.contrastText is white on #ff9800: 2.16:1. Whichever of the palette's
 * black and white reads better on warning.light is used; MUI's getContrastRatio
 * reads any colour format a palette holds.
 */
function noticeTextColor(theme: Theme): string {
  const background = theme.palette.warning.light;
  const { black, white } = theme.palette.common;
  return getContrastRatio(background, black) >= getContrastRatio(background, white) ? black : white;
}

export const PlatformNoticeBar: React.FC<PlatformNoticeBarProps> = ({
  publicWidgetToken,
}) => {
  const { t } = useLingui();
  const [notice, setNotice] = useState<PlatformNoticeResponse | null>(null);
  const failuresRef = useRef(0);
  const loggedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();

    const schedule = () => {
      if (cancelled) return;
      if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;
      const multiplier = Math.min(2 ** failuresRef.current, MAX_BACKOFF_MULTIPLIER);
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS * multiplier);
    };

    const poll = async () => {
      try {
        const result = await getPlatformNotice(publicWidgetToken, {
          signal: controller.signal,
        });
        if (cancelled) return;
        failuresRef.current = 0;
        setNotice(result);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        failuresRef.current += 1;
        // Loud to telemetry, silent to the end user - see the module doc.
        if (!loggedRef.current) {
          loggedRef.current = true;
          console.warn("[PlatformNoticeBar] platform notice unavailable", err);
        }
        // A failed read never clears an already-shown notice: the last known
        // state is more trustworthy than a network error.
      } finally {
        schedule();
      }
    };

    void poll();
    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== null) clearTimeout(timer);
    };
  }, [publicWidgetToken]);

  // The bar is a tab stop only while it scrolls (TASK-BUGFIX-WIDGET-NOTICE-TABSTOP):
  // a scrolled region must be reachable by keyboard (axe
  // scrollable-region-focusable), and a bar that fits must not add a tab stop
  // that does nothing. Measured when the notice changes and on every resize.
  const barRef = useRef<HTMLDivElement | null>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const el = barRef.current;
    if (!el) {
      setScrolls(false);
      return;
    }
    const measure = () => setScrolls(el.scrollHeight > el.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [notice]);

  if (!notice?.active) {
    // loading, error and hidden all render nothing - no DOM node, no layout shift.
    return null;
  }

  const text = selectText(notice);
  if (!text) {
    // An active notice with no text for any locale is not renderable; showing
    // an empty warning bar would be worse than showing nothing.
    return null;
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={t`Platform security notice`}
      data-testid="platform-notice-bar"
      ref={barRef}
      // A tab stop only while it scrolls (see barRef above; WCAG 2.1.1).
      tabIndex={scrolls ? 0 : undefined}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        width: "100%",
        boxSizing: "border-box",
        px: 2,
        py: 1,
        // Tokens only - no inline hex anywhere in this file.
        backgroundColor: theme.palette.warning.light,
        color: noticeTextColor(theme),
        borderBottom: `1px solid ${theme.palette.warning.main}`,
        // Painted above a shell's fixed header, which sits at the same top edge
        // in all three SPAs. Without this the bar would be covered by the very
        // chrome it has to appear over, and a legally required notice would be
        // invisible.
        position: "relative",
        zIndex: theme.zIndex.drawer + 3,
        // On a short surface a long notice must not take the room the chat
        // needs (TASK-BUGFIX-WIDGET-NOTICE-REFLOW; WCAG 1.4.10): the bar keeps
        // at most 30% of the viewport and scrolls the rest, so the whole notice
        // stays readable. On a full page the cap never binds.
        maxHeight: "30vh",
        overflowY: "auto",
        flexShrink: 0,
        // A very short viewport - the embedded widget at 320x256 is a 224 px
        // panel, where the header, the AI disclosure and the composer already
        // need 180 px (measured) - shows ONE line of the notice and scrolls the
        // rest, so the composer stays inside the panel.
        "@media (max-height: 360px)": {
          maxHeight: 28,
          py: 0.25,
        },
      })}
    >
      <WarningAmberIcon fontSize="small" aria-hidden="true" />
      <Typography variant="body2" component="p" sx={{ fontWeight: 500 }}>
        {text}
      </Typography>
    </Box>
  );
};

export default PlatformNoticeBar;
